import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

const ConnectedCareHero = () => {
  const { t } = useTranslation();
  const hasAnimated = useRef(false);
  
  const firstRowKeywords = [
    t.connectedCareHero.keywords.secondOpinion, 
    t.connectedCareHero.keywords.telemedicine, 
    t.connectedCareHero.keywords.smartNutrition, 
    t.connectedCareHero.keywords.selfManagement, 
    t.connectedCareHero.keywords.digitalTherapeutics, 
    t.connectedCareHero.keywords.eWellness
  ];
  const secondRowKeywords = [
    t.connectedCareHero.keywords.behaviorChange, 
    t.connectedCareHero.keywords.mentalHealth
  ];
  const allKeywords = [...firstRowKeywords, ...secondRowKeywords];
  const [activeKeywordIndex, setActiveKeywordIndex] = useState(0);

  // Hero headline animation
  const suffixes = [t.connectedCareHero.anytime, t.connectedCareHero.anywhere];
  const [currentSuffixIndex, setCurrentSuffixIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    hasAnimated.current = true;
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveKeywordIndex(prev => (prev + 1) % allKeywords.length);
    }, 2500); // Change every 2.5 seconds

    return () => clearInterval(interval);
  }, [allKeywords.length]);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      setCurrentSuffixIndex(prev => (prev + 1) % suffixes.length);
    }, 1750); // Change every 1.75 seconds

    return () => clearInterval(interval);
  }, [prefersReducedMotion, suffixes.length]);
  return <section className="relative pt-40 pb-32 overflow-hidden">
      {/* Healthcare professional background */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: `url(/lovable-uploads/e1aa93b9-184a-4db5-93e2-f30156e3e8bb.png)`
    }} />
      {/* Full-bleed overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/50 via-primary/40 to-secondary/50" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto text-center">
          {/* Main headline */}
          <motion.h1 initial={hasAnimated.current ? false : {
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
          }} className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight lg:text-6xl flex flex-wrap items-baseline justify-center" style={{
          fontSize: 'clamp(1.75rem, 4.5vw, 4rem)' // Smaller base size for mobile
        }} aria-live="polite" aria-atomic="true">
            {prefersReducedMotion ? t.connectedCareHero.fullHeadline : <>
                <span className="py-[10px]">{t.connectedCareHero.healthForAll}</span>
                <span className="inline-block whitespace-nowrap">
                  <span key={currentSuffixIndex} style={{
                    animationDuration: '1s'
                  }} className="animate-fade-in py-[10px]">
                    {suffixes[currentSuffixIndex]}
                  </span>
                </span>
              </>}
          </motion.h1>
          
          {/* Subheadline */}
          <motion.p initial={hasAnimated.current ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl md:text-2xl text-white/90 mb-20 font-normal max-w-4xl mx-auto py-[5px]">
            {t.connectedCareHero.subtitle}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mt-8" initial={hasAnimated.current ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }}>
            <Link to="/services" className="button-primary group" aria-label={t.connectedCareHero.exploreServices}>
              {t.connectedCareHero.exploreServices}
            </Link>
            <a href="https://vida24.eu/" target="_blank" rel="noopener noreferrer" className="button-secondary" aria-label={t.connectedCareHero.discoverVida24}>
              {t.connectedCareHero.discoverVida24}
            </a>
          </motion.div>
        
          {/* Keywords */}
          <motion.div className="max-w-5xl mx-auto space-y-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }}>
            {/* First Row */}
            
            
            {/* Second Row */}
            
          </motion.div>
        </div>
      </div>
    </section>;
};
export default ConnectedCareHero;